
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Navbar from '../../components/Navbar';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline'; // Assuming you have heroicons

const AdminSliders = () => {
    const [sliders, setSliders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        link: '',
        order: 0,
        is_active: true,
        image: null
    });
    // For edit mode
    const [editingId, setEditingId] = useState(null);

    const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : process.env.REACT_APP_API_URL;
    // Helper for images
    const STORAGE_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000' : process.env.REACT_APP_API_URL?.replace('/api', '');

    useEffect(() => {
        fetchSliders();
    }, []);

    const fetchSliders = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/sliders`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                setSliders(data);
            } else {
                toast.error('Failed to fetch sliders');
            }
        } catch (error) {
            console.error('Error fetching sliders:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('title', formData.title);
        if (formData.link) data.append('link', formData.link);
        data.append('order', formData.order);
        data.append('is_active', formData.is_active ? '1' : '0');
        if (formData.image) data.append('image', formData.image);

        try {
            let url = `${API_URL}/admin/sliders`;
            // For updates with file upload, we use POST
            if (editingId) {
                url = `${API_URL}/admin/sliders/${editingId}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                },
                body: data
            });

            if (response.ok) {
                toast.success(`Slider ${editingId ? 'updated' : 'added'} successfully`);
                setFormData({ title: '', link: '', order: 0, is_active: true, image: null });
                setEditingId(null);
                // Reset file input
                document.getElementById('slider-image-input').value = "";
                fetchSliders();
            } else {
                const err = await response.json();
                toast.error(err.message || 'Failed to save slider');
            }
        } catch (error) {
            console.error('Error saving slider:', error);
            toast.error('Error saving slider');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this slider?')) return;
        try {
            const response = await fetch(`${API_URL}/admin/sliders/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Accept': 'application/json'
                }
            });
            if (response.ok) {
                toast.success('Slider deleted');
                fetchSliders();
            } else {
                toast.error('Failed to delete slider');
            }
        } catch (error) {
            toast.error('Error deleting slider');
        }
    };

    const handleEdit = (slider) => {
        setEditingId(slider.id);
        setFormData({
            title: slider.title || '',
            link: slider.link || '',
            order: slider.order || 0,
            is_active: slider.is_active,
            image: null // Keep null, if user wants to change they upload new one
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({ title: '', link: '', order: 0, is_active: true, image: null });
        document.getElementById('slider-image-input').value = "";
    }

    return (
        <div className="min-h-screen bg-cover bg-center bg-fixed" style={{ backgroundImage: "url('/images/trade-bg.jpg')" }}>
            <div className="min-h-screen bg-black/40 w-full pb-10">
                <Navbar userType="admin" />
                <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Hero Sliders</h1>

                    {/* Form */}
                    <div className="bg-white shadow rounded-lg p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-4">{editingId ? 'Edit Slider' : 'Add New Slider'}</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Image {editingId && '(Leave empty to keep current)'}</label>
                                    <input
                                        id="slider-image-input"
                                        type="file"
                                        name="image"
                                        accept="image/*"
                                        onChange={handleInputChange}
                                        required={!editingId}
                                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Title (Optional)</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Link URL (Optional)</label>
                                    <input
                                        type="url"
                                        name="link"
                                        value={formData.link}
                                        onChange={handleInputChange}
                                        placeholder="https://"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                    />
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700">Order</label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm border p-2"
                                        />
                                    </div>
                                    <div className="flex items-center pt-6">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleInputChange}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <label className="ml-2 block text-sm text-gray-900">Active</label>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3">
                                {editingId && (
                                    <button
                                        type="button"
                                        onClick={handleCancelEdit}
                                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {editingId ? 'Update Slider' : 'Add Slider'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* List */}
                    {loading ? <p>Loading...</p> : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sliders.map((slider) => (
                                <div key={slider.id} className="bg-white rounded-lg shadow overflow-hidden relative group">
                                    <div className="aspect-w-16 aspect-h-9 h-48 bg-gray-200">
                                        <img
                                            src={`${STORAGE_URL}${slider.image_path}`}
                                            alt={slider.title}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900">{slider.title || 'No Title'}</h3>
                                                <p className="text-sm text-gray-500">Order: {slider.order}</p>
                                            </div>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${slider.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {slider.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {slider.link && (
                                            <a href={slider.link} target="_blank" rel="noopener noreferrer" className="block mt-2 text-sm text-blue-600 hover:text-blue-500 truncate">
                                                {slider.link}
                                            </a>
                                        )}
                                        <div className="mt-4 flex justify-end space-x-2">
                                            <button
                                                onClick={() => handleEdit(slider)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(slider.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminSliders;
